// ============================================================================
// Auto-generated from the backend spec (docs/spec.md in the telemetrydrops/api repo)
// Last synced: 2026-04-06
// Do not manually edit — re-run api-client-sync skill instead.
// ============================================================================

import type {
	AdminStudentsParams,
	CheckoutRequest,
	CheckoutResponse,
	Course,
	CourseContent,
	CourseDetail,
	CreateCourseRequest,
	CreateCourseResponse,
	CreateDiscountRequest,
	CreateEnrollmentRequest,
	CreateEnrollmentResponse,
	CreateModuleRequest,
	CreateModuleResponse,
	CreateSectionRequest,
	CreateSectionResponse,
	DashboardStats,
	DisableEnrollmentsRequest,
	DisableEnrollmentsResponse,
	Discount,
	EnableEnrollmentRequest,
	EnableEnrollmentResponse,
	Enrollment,
	MeResponse,
	Progress,
	Quiz,
	QuizResult,
	StatusResponse,
	StudentDetail,
	StudentSummary,
	SubmitQuizRequest,
	UpdateCourseRequest,
	UpdateDiscountRequest,
	UpdateModuleRequest,
	UpdateProgressRequest,
	UpdateSectionRequest,
	UpsertQuizRequest,
	UpsertQuizResponse,
} from "./api-types";
import { ApiError, NetworkError } from "./api-types";

const API_BASE = import.meta.env.VITE_API_URL || "https://api.telemetrydrops.com";

let getTokenFn: (() => Promise<string | null>) | null = null;

/**
 * Register the Clerk auth token getter.
 * Call this once at app startup with `useAuth().getToken`.
 */
export function setAuthTokenGetter(fn: () => Promise<string | null>) {
	getTokenFn = fn;
}

// ---------------------------------------------------------------------------
// Backend connectivity tracking
// ---------------------------------------------------------------------------

type ConnectivityListener = (online: boolean) => void;
const connectivityListeners = new Set<ConnectivityListener>();
let backendOnline = true;

export function getBackendOnline() {
	return backendOnline;
}

export function onBackendConnectivityChange(listener: ConnectivityListener): () => void {
	connectivityListeners.add(listener);
	return () => connectivityListeners.delete(listener);
}

function setBackendOnline(online: boolean) {
	if (backendOnline === online) return;
	backendOnline = online;
	for (const listener of connectivityListeners) {
		listener(online);
	}
}

/**
 * Core fetch wrapper that injects the Clerk JWT and handles error responses.
 * All API functions below delegate to this.
 */
async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
	if (import.meta.env.PROD && new URL(API_BASE).protocol !== "https:") {
		throw new Error("The production API URL must use HTTPS.");
	}
	const token = getTokenFn ? await getTokenFn() : null;
	let res: Response;
	try {
		res = await fetch(`${API_BASE}${path}`, {
			...options,
			headers: {
				"Content-Type": "application/json",
				...(token ? { Authorization: `Bearer ${token}` } : {}),
				...options?.headers,
			},
		});
	} catch (err) {
		setBackendOnline(false);
		throw new NetworkError(err);
	}
	setBackendOnline(true);
	if (!res.ok) {
		const body = await res.json().catch(() => ({
			error: { message: res.statusText },
		}));
		throw new ApiError(
			res.status,
			body.error?.code ?? "unknown",
			body.error?.message ?? res.statusText,
		);
	}
	return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Public endpoints (no auth required)
// ---------------------------------------------------------------------------

/** GET /courses — List published courses, optionally filtered by language. */
export const getCourses = (language?: string) =>
	apiFetch<{ courses: Course[] }>(`/courses${language ? `?language=${language}` : ""}`);

/** GET /courses/:courseId — Get course detail with sections and modules (no video IDs). */
export const getCourse = (courseId: string) => apiFetch<CourseDetail>(`/courses/${courseId}`);

/** POST /checkout — Create Stripe hosted checkout session. */
export const createCheckout = (body: CheckoutRequest) =>
	apiFetch<CheckoutResponse>("/checkout", {
		method: "POST",
		body: JSON.stringify(body),
	});

// ---------------------------------------------------------------------------
// Student endpoints (auth required)
// ---------------------------------------------------------------------------

/** GET /me — Get authenticated user's profile and summary stats. */
export const getMe = () => apiFetch<MeResponse>("/me");

/** PATCH /me — Update user preferences (language). */
export const updateMe = (body: { language: string }) =>
	apiFetch<{ language: string }>("/me", {
		method: "PATCH",
		body: JSON.stringify(body),
	});

/** GET /me/enrollments — List user's enrollments with progress summary. */
export const getMyEnrollments = () => apiFetch<{ enrollments: Enrollment[] }>("/me/enrollments");

/** GET /me/courses/:courseId — Get full course content with signed video URLs (enrolled only). */
export const getMyCourse = (courseId: string) => apiFetch<CourseContent>(`/me/courses/${courseId}`);

/** PUT /me/progress/:moduleId — Update video progress for a module. */
export const updateProgress = (moduleId: string, body: UpdateProgressRequest) =>
	apiFetch<Progress>(`/me/progress/${moduleId}`, {
		method: "PUT",
		body: JSON.stringify(body),
	});

/** GET /me/quizzes/:moduleId — Get quiz questions without answers (enrolled only). */
export const getQuiz = (moduleId: string) => apiFetch<Quiz>(`/me/quizzes/${moduleId}`);

/** POST /me/quizzes/:moduleId/submit — Submit quiz answers and get graded results. */
export const submitQuiz = (moduleId: string, body: SubmitQuizRequest) =>
	apiFetch<QuizResult>(`/me/quizzes/${moduleId}/submit`, {
		method: "POST",
		body: JSON.stringify(body),
	});

// ---------------------------------------------------------------------------
// Admin endpoints (admin role required)
// ---------------------------------------------------------------------------

/** GET /admin/dashboard — Get admin dashboard statistics. */
export const getAdminDashboard = () => apiFetch<DashboardStats>("/admin/dashboard");

/** GET /admin/students — List students with optional search, course filter, and pagination. */
export const getAdminStudents = (params?: AdminStudentsParams) => {
	const searchParams = new URLSearchParams();
	if (params?.search) searchParams.set("search", params.search);
	if (params?.course_id) searchParams.set("course_id", params.course_id);
	if (params?.limit != null) searchParams.set("limit", String(params.limit));
	if (params?.offset != null) searchParams.set("offset", String(params.offset));
	const qs = searchParams.toString();
	return apiFetch<{ students: StudentSummary[]; total: number }>(
		`/admin/students${qs ? `?${qs}` : ""}`,
	);
};

/** GET /admin/students/:userId — Get full student detail with enrollments, progress, and quiz history. */
export const getAdminStudent = (userId: string) =>
	apiFetch<StudentDetail>(`/admin/students/${userId}`);

/** POST /admin/enrollments — Manually enroll a user by email. */
export const createEnrollment = (body: CreateEnrollmentRequest) =>
	apiFetch<CreateEnrollmentResponse>("/admin/enrollments", {
		method: "POST",
		body: JSON.stringify(body),
	});

/** POST /admin/discounts — Create a Stripe discount code. */
export const createDiscount = (body: CreateDiscountRequest) =>
	apiFetch<Discount>("/admin/discounts", {
		method: "POST",
		body: JSON.stringify(body),
	});

/** GET /admin/discounts — List active discounts from Stripe. */
export const getDiscounts = () => apiFetch<{ discounts: Discount[] }>("/admin/discounts");

/** GET /admin/discounts/:discountId — Get a single discount by Stripe promo ID. */
export const getDiscount = (discountId: string) =>
	apiFetch<Discount>(`/admin/discounts/${discountId}`);

/** PUT /admin/discounts/:discountId — Update a discount's active status or name. */
export const updateDiscount = (discountId: string, body: UpdateDiscountRequest) =>
	apiFetch<Discount>(`/admin/discounts/${discountId}`, {
		method: "PUT",
		body: JSON.stringify(body),
	});

/** DELETE /admin/discounts/:discountId — Deactivate a discount (reversible via update). */
export const deactivateDiscount = (discountId: string) =>
	apiFetch<Discount>(`/admin/discounts/${discountId}`, { method: "DELETE" });

// --- Course CRUD ---

/** POST /admin/courses — Create a new course. */
export const createCourse = (body: CreateCourseRequest) =>
	apiFetch<CreateCourseResponse>("/admin/courses", {
		method: "POST",
		body: JSON.stringify(body),
	});

/** PUT /admin/courses/:courseId — Update an existing course. */
export const updateCourse = (courseId: string, body: UpdateCourseRequest) =>
	apiFetch<StatusResponse>(`/admin/courses/${courseId}`, {
		method: "PUT",
		body: JSON.stringify(body),
	});

/** DELETE /admin/courses/:courseId — Archive a course (soft delete). */
export const archiveCourse = (courseId: string) =>
	apiFetch<StatusResponse>(`/admin/courses/${courseId}`, { method: "DELETE" });

// --- Section CRUD ---

/** POST /admin/courses/:courseId/sections — Add a section to a course. */
export const createSection = (courseId: string, body: CreateSectionRequest) =>
	apiFetch<CreateSectionResponse>(`/admin/courses/${courseId}/sections`, {
		method: "POST",
		body: JSON.stringify(body),
	});

/** PUT /admin/sections/:sectionId — Update a section. */
export const updateSection = (sectionId: string, body: UpdateSectionRequest) =>
	apiFetch<StatusResponse>(`/admin/sections/${sectionId}`, {
		method: "PUT",
		body: JSON.stringify(body),
	});

/** DELETE /admin/sections/:sectionId — Remove an empty section. */
export const deleteSection = (sectionId: string) =>
	apiFetch<StatusResponse>(`/admin/sections/${sectionId}`, { method: "DELETE" });

/** POST /admin/courses/:courseId/sections/reorder — Reorder sections by ID array. */
export const reorderSections = (courseId: string, body: { section_ids: string[] }) =>
	apiFetch<StatusResponse>(`/admin/courses/${courseId}/sections/reorder`, {
		method: "POST",
		body: JSON.stringify(body),
	});

// --- Module CRUD ---

/** POST /admin/sections/:sectionId/modules — Add a module to a section. */
export const createModule = (sectionId: string, body: CreateModuleRequest) =>
	apiFetch<CreateModuleResponse>(`/admin/sections/${sectionId}/modules`, {
		method: "POST",
		body: JSON.stringify(body),
	});

/** PUT /admin/modules/:moduleId — Update a module. */
export const updateModule = (moduleId: string, body: UpdateModuleRequest) =>
	apiFetch<StatusResponse>(`/admin/modules/${moduleId}`, {
		method: "PUT",
		body: JSON.stringify(body),
	});

/** DELETE /admin/modules/:moduleId — Remove a module. */
export const deleteModule = (moduleId: string) =>
	apiFetch<StatusResponse>(`/admin/modules/${moduleId}`, { method: "DELETE" });

/** POST /admin/sections/:sectionId/modules/reorder — Reorder modules by ID array. */
export const reorderModules = (sectionId: string, body: { module_ids: string[] }) =>
	apiFetch<StatusResponse>(`/admin/sections/${sectionId}/modules/reorder`, {
		method: "POST",
		body: JSON.stringify(body),
	});

// --- Quiz CRUD ---

/** PUT /admin/modules/:moduleId/quiz — Create or update a quiz for a module. */
export const upsertQuiz = (moduleId: string, body: UpsertQuizRequest) =>
	apiFetch<UpsertQuizResponse>(`/admin/modules/${moduleId}/quiz`, {
		method: "PUT",
		body: JSON.stringify(body),
	});

/** DELETE /admin/modules/:moduleId/quiz — Remove a quiz from a module. */
export const deleteQuiz = (moduleId: string) =>
	apiFetch<StatusResponse>(`/admin/modules/${moduleId}/quiz`, {
		method: "DELETE",
	});

/** POST /admin/students/:userId/enrollments/disable — Disable one or all active enrollments. */
export const disableEnrollments = (userId: string, body: DisableEnrollmentsRequest) =>
	apiFetch<DisableEnrollmentsResponse>(
		`/admin/students/${encodeURIComponent(userId)}/enrollments/disable`,
		{
			method: "POST",
			body: JSON.stringify(body),
		},
	);

/** POST /admin/students/:userId/enrollments/enable — Restore a disabled course enrollment. */
export const enableEnrollment = (userId: string, body: EnableEnrollmentRequest) =>
	apiFetch<EnableEnrollmentResponse>(
		`/admin/students/${encodeURIComponent(userId)}/enrollments/enable`,
		{
			method: "POST",
			body: JSON.stringify(body),
		},
	);
